// ============================================================================
// FILE: BOQOperationMappingView.js
// MATERIAL CENTRIC + EXCEL NAVIGATION + SAFE STATE (TaskFlow ERP)
// ============================================================================

import React, { useEffect, useState, useMemo, useRef } from "react";
import axiosClient from "../../../../api/axiosClient";

const BOQOperationMappingView = ({ orderItemId }) => {

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [mapping, setMapping] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [boqId, setBoqId] = useState(null);

  // Excel navigation refs
  const inputRefs = useRef({});

  // ---------------------------------------------------------------------------
  // LOAD DATA
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!orderItemId) {
      setLoading(false);
      return;
    }
    loadMappingData();
  }, [orderItemId]);

  const loadMappingData = async () => {
    try {
      setLoading(true);

      const res = await axiosClient.get(
        `/api/ppe/routing/order-items/${orderItemId}/boq-operation-mapping`
      );

      const data = res.data?.data || {};
      setBoqId(data.boq_id);

      setOperations(data.operations || []);
      setMaterials(data.materials || []);

      const matrix = {};
      (data.mappings || []).forEach(m => {
        if (!matrix[m.routing_assignment_operation_id]) {
          matrix[m.routing_assignment_operation_id] = {};
        }
        matrix[m.routing_assignment_operation_id][m.boq_line_id] =
          m.assigned_quantity;
      });

      setMapping(matrix);

    } catch (err) {
      console.error(err);
      setError("Failed to load BOQ operation mapping");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // UPDATE QUANTITY (FIXED IMMUTABILITY)
  // ---------------------------------------------------------------------------
  const updateQuantity = (operationId, boqLineId, value) => {
    const qty = parseFloat(value || 0);

    setMapping(prev => ({
      ...prev,
      [operationId]: {
        ...(prev[operationId] || {}),
        [boqLineId]: qty
      }
    }));
  };

  // ---------------------------------------------------------------------------
  // EXCEL NAVIGATION
  // ---------------------------------------------------------------------------
  const registerInputRef = (r, c, el) => {
    if (!inputRefs.current[r]) inputRefs.current[r] = {};
    inputRefs.current[r][c] = el;
  };

  const moveFocus = (r, c) => {
    const el = inputRefs.current?.[r]?.[c];
    if (el) el.focus();
  };

  const handleKeyNavigation = (e, r, c) => {
    switch (e.key) {
      case "Enter":
      case "ArrowRight":
        e.preventDefault();
        moveFocus(r, c + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        moveFocus(r, c - 1);
        break;
      case "ArrowDown":
        e.preventDefault();
        moveFocus(r + 1, c);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveFocus(r - 1, c);
        break;
      default:
        break;
    }
  };

  // ---------------------------------------------------------------------------
  // TOTALS
  // ---------------------------------------------------------------------------
  const materialTotals = useMemo(() => {
    const totals = {};
    Object.values(mapping).forEach(op => {
      Object.entries(op).forEach(([boqLineId, qty]) => {
        totals[boqLineId] = (totals[boqLineId] || 0) + Number(qty || 0);
      });
    });
    return totals;
  }, [mapping]);

  const isValidAllocation = () =>
    materials.every(mat =>
      (materialTotals[mat.boq_line_id] || 0) <= mat.quantity
    );

  // ---------------------------------------------------------------------------
  // SAVE
  // ---------------------------------------------------------------------------
  const handleSave = async () => {

    if (!isValidAllocation()) {
      alert("Allocated quantity exceeds BOQ quantity.");
      return;
    }

    try {
      setSaving(true);

      const payload = [];

      Object.entries(mapping).forEach(([operationId, mats]) => {
        Object.entries(mats).forEach(([boqLineId, qty]) => {
          if (qty > 0) {
            payload.push({
              operation_id: Number(operationId),
              boq_line_id: Number(boqLineId),
              assigned_quantity: qty
            });
          }
        });
      });

      await axiosClient.post(
        `/api/ppe/routing/boq-operation-mapping/save`,
        { 
          boq_id: boqId,
          mappings: payload }
      );

      alert("Mapping saved successfully");

    } catch {
      alert("Failed to save mapping");
    } finally {
      setSaving(false);
    }
  };

  const getQty = (operationId, boqLineId) =>
    mapping?.[operationId]?.[boqLineId] ?? "";

  // ---------------------------------------------------------------------------
  // UI STATES
  // ---------------------------------------------------------------------------
  if (loading) return <div className="p-4">Loading mapping...</div>;
  if (error) return <div className="p-4 text-danger">{error}</div>;

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <div className="card shadow-sm">

      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">BOQ Operation Material Mapping</h5>

        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Mapping"}
        </button>
      </div>

      <div className="card-body table-responsive" style={{ maxHeight: "65vh" }}>
        <table className="table table-bordered align-middle">

          <thead className="table-light sticky-top">
            <tr>
              <th style={{ minWidth: 260 }}>Material</th>
              <th>BOQ Qty</th>

              {operations.map(op => (
                <th key={op.operation_id} className="text-center">
                  {op.operation_seq_no}
                  <div className="small text-muted">
                    {op.operation_name}
                  </div>
                </th>
              ))}

              <th>Total</th>
            </tr>
          </thead>

          <tbody>
            {materials.map((mat, rowIndex) => {

              const total = materialTotals[mat.boq_line_id] || 0;
              const exceeded = total > mat.quantity;

              return (
                <tr key={mat.boq_line_id}>
                  <td><strong>{mat.material_name}</strong></td>
                  <td>{mat.quantity}</td>

                  {operations.map((op, colIndex) => (
                    <td key={`${mat.boq_line_id}_${op.routing_assignment_operation_id}`}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={getQty(op.routing_assignment_operation_id, mat.boq_line_id)}
                        ref={(el) =>
                          registerInputRef(rowIndex, colIndex, el)
                        }
                        onKeyDown={(e) =>
                          handleKeyNavigation(e, rowIndex, colIndex)
                        }
                        onChange={(e) =>
                          updateQuantity(
                             op.routing_assignment_operation_id,
                              mat.boq_line_id,
                              e.target.value
                          )
                        }
                        min="0"
                        step="0.01"
                      />
                    </td>
                  ))}

                  <td className={exceeded ? "text-danger fw-bold" : "fw-semibold"}>
                    {total}
                  </td>
                </tr>
              );
            })}
          </tbody>

        </table>
      </div>
    </div>
  );
};

export default BOQOperationMappingView;